import { useQuery } from '@tanstack/react-query';

import {
  getRegions,
  getWeeklySeries,
  NATIONAL_REGION_CODE,
  resolvePropertyRegion,
  resolveRegionLabel,
} from '@/api/timber-prices';
import type { PropertyForestContext } from '@/api/propertyForestContext';
import type { Language } from '@/i18n';

const LANDING_ASSORTMENTS = ['N1', 'N4'] as const;
const LANDING_REGION = NATIONAL_REGION_CODE;

export interface LandingTimberPriceSeries {
  points: Array<Record<string, string | number | null>>;
  source: string;
  latestWeek: string | null;
  region: string;
  regionLabel: string;
  harvestType: string;
}

function propertyCentre(
  context: PropertyForestContext | null,
): { lat: number; lng: number } | null {
  if (!context?.bbox) return null;
  const [minLng, minLat, maxLng, maxLat] = context.bbox;
  return {
    lat: (minLat + maxLat) / 2,
    lng: (minLng + maxLng) / 2,
  };
}

export function useLandingTimberPriceSeries(
  propertyId: string,
  context: PropertyForestContext | null,
  locale: Language,
) {
  const centre = propertyCentre(context);
  return useQuery({
    queryKey: [
      'story',
      'timber-prices',
      'landing',
      propertyId,
      centre?.lat ?? null,
      centre?.lng ?? null,
      locale,
      '0',
      LANDING_ASSORTMENTS,
    ],
    queryFn: async (): Promise<LandingTimberPriceSeries> => {
      const regions = await getRegions();
      const resolvedRegion = centre ? await resolvePropertyRegion(centre.lat, centre.lng) : null;
      const selectedRegion =
        regions.find((region) => region.code === resolvedRegion) ??
        regions.find((region) => region.code === LANDING_REGION);
      if (!selectedRegion) {
        throw new Error('Timber-price region list does not include a national region.');
      }
      const responses = await Promise.all(
        LANDING_ASSORTMENTS.map((assortment) =>
          getWeeklySeries({
            region: selectedRegion.code,
            harvest_type: '0',
            assortment,
            // The hero shows a real multi-year market trend. The endpoint caps
            // pages at 500; the current LUKE sync supplies roughly three years.
            page_size: 500,
          }),
        ),
      );
      const rows = new Map<string, Record<string, string | number | null>>();
      responses.forEach((response, responseIndex) => {
        const assortment = LANDING_ASSORTMENTS[responseIndex];
        response.results.data.forEach((point) => {
          const row = rows.get(point.week_code) ?? { week: point.week_code };
          row[assortment] = Number(point.price_eur_per_m3);
          rows.set(point.week_code, row);
        });
      });
      const points = [...rows.values()].sort((left, right) =>
        String(left.week).localeCompare(String(right.week)),
      );
      const metadata = responses[0].results.metadata;
      return {
        points,
        source: metadata.source,
        latestWeek: metadata.latest_week ?? null,
        region: selectedRegion.code,
        regionLabel: resolveRegionLabel(selectedRegion, locale),
        harvestType: '0',
      };
    },
    staleTime: 30 * 60 * 1000,
  });
}
